import * as React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Box,
    TextField,
    Checkbox,
    OutlinedInput,
    Typography,
    Select,
    MenuItem,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Paper,
    Divider,
} from '@mui/material';
import { AssessmentQuestions } from '../../../../utils/assessmentQuestions';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { AssessmentTypeEnum } from '../../../../Enum/AssessmentTypeEnum';
import { AssessmentCategory, Question } from '../../../../Models/Assessment/AssessmentCategory.model';
import { CommentEntry } from '../../../../Models/Assessment/CommentEntry.model';
import classes from '../PhysicalHealthPages/Styles';
import { SelectionType } from '../../../../Enum/SelectionTypeEnum';
import { copyObject } from '../../../../utils/common';
import Authorization from '../../../../utils/Authorization';
import { ProviderId } from '../../../../utils/constants';
import {
    updateQuestionOption,
    updateSubOption,
    updateSubSubOption,
    updateSubQuestionOption,
    updateDropdownAnswer,
    updateQuestionDate,
    updateQuestionAnswerText,
    updateCategoryComments,
    updateSubOptionTextAnswer,
    updateSubSubOptionTextAnswer,
    updateSubQuestionAnswerText,
    updateSubQuestionOptionSubOptionTextAnswer,
    updateSubOptionSubQuestionAnswerText,
} from '../../../../utils/structuralUpdate';
import SubOptionsRow from '../PhysicalHealthPages/SubOptionsRow';
import SubQuestionsRow from './SubQuestionsRow';
import { isAIIcon } from '../../Assessment';

type QuestionRowProps = {
    question: Question;
    qIndex: number;
    colSpan: number;
    showDatePicker: boolean;
    scoreTotal: number;
    onUpdateAnswer: (isSelected: boolean, questionIndex: number, optionIndex: number) => void;
    onUpdateDropdownAnswer: (selectedValue: string | string[], questionIndex: number) => void;
    onUpdateDate: (date: Date | null, questionIndex: number) => void;
    onUpdateSubAnswer: (isSelected: boolean, questionIndex: number, optionIndex: number, subOptionIndex: number) => void;
    onUpdateSubOptionName: (name: string, questionIndex: number, optionIndex: number, subOptionIndex: number) => void;
    onUpdateSubSubAnswer: (isSelected: boolean, questionIndex: number, optionIndex: number, subOptionIndex: number, subSubOptionIndex: number) => void;
    onUpdateSubSubOptionName: (name: string, questionIndex: number, optionIndex: number, subOptionIndex: number, subSubOptionIndex: number) => void;
    onUpdateSubOptionSubQuestionAnswerText: (
        value: string | Date | null,
        questionIndex: number,
        optionIndex: number,
        subOptionIndex: number,
        subQuestionIndex: number
    ) => void;
    onUpdateSubOptionSubQuestionAnswer: (
        isSelected: boolean,
        questionIndex: number,
        optionIndex: number,
        subOptionIndex: number,
        subQuestionIndex: number,
        subQuestionOptionIndex: number
    ) => void;
    onUpdateSubQuestionAnswer: (
        isSelected: boolean,
        questionIndex: number,
        optionIndex: number,
        subQuestionIndex: number,
        subQuestionOptionIndex: number
    ) => void;
    onUpdateSubQuestionAnswerText: (value: string | Date | null, questionIndex: number, optionIndex: number, subQuestionIndex: number) => void;
    onUpdateSubQuestionSubOptionAnswer: (
        isSelected: boolean,
        questionIndex: number,
        optionIndex: number,
        subQuestionIndex: number,
        subQuestionOptionIndex: number,
        subOptionIndex: number
    ) => void;
    onUpdateSubQuestionOptionSubOptionName: (
        value: string,
        questionIndex: number,
        optionIndex: number,
        subQuestionIndex: number,
        subQuestionOptionIndex: number,
        subOptionIndex: number
    ) => void;
    onChangeAnswerText: (answerText: string, questionIndex: number) => void;
};

const QuestionRow: React.FC<QuestionRowProps> = React.memo(
    ({
        question,
        qIndex,
        colSpan,
        showDatePicker,
        scoreTotal,
        onUpdateAnswer,
        onUpdateDropdownAnswer,
        onUpdateDate,
        onUpdateSubAnswer,
        onUpdateSubOptionName,
        onUpdateSubSubAnswer,
        onUpdateSubSubOptionName,
        onUpdateSubOptionSubQuestionAnswerText,
        onUpdateSubOptionSubQuestionAnswer,
        onUpdateSubQuestionAnswer,
        onUpdateSubQuestionAnswerText,
        onUpdateSubQuestionSubOptionAnswer,
        onUpdateSubQuestionOptionSubOptionName,
        onChangeAnswerText,
    }) => {
        const visibleOptions = React.useMemo(() => question.Options?.filter((option) => !option.IsArchived) ?? [], [question.Options]);
        const hasNoneOption = React.useMemo(() => visibleOptions.some((option) => option.Name === 'None'), [visibleOptions]);
        const isNoneSelected = React.useMemo(() => visibleOptions.some((option) => option.Name === 'None' && option.IsSelected), [visibleOptions]);
        const dropdownValue = React.useMemo(() => {
            if (question.SelectionType === SelectionType.MultiSelectWithDropdown) {
                return visibleOptions.filter((o) => o.IsSelected).map((o) => o.Name);
            }
            return visibleOptions.find((o) => o.IsSelected)?.Name || '';
        }, [question.SelectionType, visibleOptions]);

        return (
            <React.Fragment>
                <TableRow>
                    <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {question.Description}
                            {question.IsAIAnswer && isAIIcon(question)}
                        </Box>
                    </TableCell>
                    {question.SelectionType === SelectionType.SingleSelect || question.SelectionType === SelectionType.MultiSelect ? (
                        visibleOptions.map((option, oIndex) => (
                            <TableCell key={`body_${option.Name}_${oIndex}`} align="center" style={{ verticalAlign: 'top' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                                        {option.Name}
                                    </Typography>
                                    <Checkbox
                                        checked={option.IsSelected}
                                        size="small"
                                        disabled={hasNoneOption && isNoneSelected && option.Name !== 'None'}
                                        radioGroup={question.Description}
                                        onChange={(e) => onUpdateAnswer(e.currentTarget.checked, qIndex, oIndex)}
                                        icon={question.SelectionType === SelectionType.SingleSelect ? <PanoramaFishEyeIcon /> : undefined}
                                        checkedIcon={question.SelectionType === SelectionType.SingleSelect ? <RadioButtonCheckedIcon /> : undefined}
                                    />
                                </Box>
                            </TableCell>
                        ))
                    ) : question.SelectionType === SelectionType.Text ? (
                        <TableCell key={`body_${question.Description}_${qIndex}`} align="center" colSpan={3}>
                            <OutlinedInput
                                className="x-small-text-input hide-textinput-label"
                                classes={{ root: '' }}
                                value={question.AnswerText ?? ''}
                                fullWidth={true}
                                size="small"
                                onChange={(e) => onChangeAnswerText(e.currentTarget.value, qIndex)}
                            />
                        </TableCell>
                    ) : question.SelectionType === SelectionType.DropdownWithSingleSelect ||
                      question.SelectionType === SelectionType.MultiSelectWithDropdown ? (
                        <TableCell key={`body_${question.Description}_${qIndex}`} align="center" colSpan={3}>
                            <Select
                                size="small"
                                fullWidth
                                multiple={question.SelectionType === SelectionType.MultiSelectWithDropdown}
                                value={dropdownValue}
                                onChange={(e) => onUpdateDropdownAnswer(e.target.value, qIndex)}
                                renderValue={(selected) => (Array.isArray(selected) ? selected.join(', ') : (selected as string))}
                            >
                                {visibleOptions.map((option) => (
                                    <MenuItem key={option.Id} value={option.Name}>
                                        {question.SelectionType === SelectionType.MultiSelectWithDropdown && (
                                            <Checkbox checked={option.IsSelected} size="small" />
                                        )}
                                        {option.Name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </TableCell>
                    ) : null}
                    {showDatePicker && (
                        <TableCell align="right">
                            {question.Options?.some((option) => option.Name === 'Yes' && option.IsSelected) ? (
                                <DesktopDatePicker
                                    value={question.Date ? new Date(question.Date) : null}
                                    views={['month', 'year']}
                                    format="MMM yyyy"
                                    onChange={(date) => onUpdateDate(date, qIndex)}
                                    slotProps={{
                                        textField: { sx: classes.datePickertextField, placeholder: '', size: 'small' },
                                    }}
                                />
                            ) : null}
                        </TableCell>
                    )}
                </TableRow>
                <SubOptionsRow
                    question={question}
                    qIndex={qIndex}
                    colSpan={colSpan}
                    onUpdateSubAnswer={onUpdateSubAnswer}
                    onUpdateSubOptionName={onUpdateSubOptionName}
                    onUpdateSubSubAnswer={onUpdateSubSubAnswer}
                    onUpdateSubSubOptionName={onUpdateSubSubOptionName}
                    onUpdateSubOptionSubQuestionAnswerText={onUpdateSubOptionSubQuestionAnswerText}
                    onUpdateSubOptionSubQuestionAnswer={onUpdateSubOptionSubQuestionAnswer}
                />
                <SubQuestionsRow
                    question={question}
                    qIndex={qIndex}
                    colSpan={colSpan}
                    scoreTotal={scoreTotal}
                    onUpdateSubQuestionAnswer={onUpdateSubQuestionAnswer}
                    onUpdateSubQuestionAnswerText={onUpdateSubQuestionAnswerText}
                    onUpdateSubQuestionSubOptionAnswer={onUpdateSubQuestionSubOptionAnswer}
                    onUpdateSubQuestionOptionSubOptionName={onUpdateSubQuestionOptionSubOptionName}
                />
            </React.Fragment>
        );
    }
);

QuestionRow.displayName = 'QuestionRow';

/**
 * Mutates an options array in place to enforce "None" toggle rules:
 * - Selecting "None" deselects everything else.
 * - Selecting a non-"None" option deselects "None".
 * - Single-select deselects all non-target options; multi-select toggles only the target.
 */
function applyNoneToggle(
    options: { Name?: string; IsSelected: boolean; IsArchived?: boolean }[],
    targetIdx: number,
    isSelected: boolean,
    isMultiSelect: boolean
): void {
    const target = options[targetIdx];
    if (!target) return;

    const isNoneOption = target.Name === 'None';

    if (isNoneOption && isSelected) {
        options.forEach((opt, i) => {
            if (opt.IsArchived) return;
            opt.IsSelected = i === targetIdx;
        });
    } else {
        if (isSelected && !isNoneOption) {
            options.forEach((opt) => {
                if (opt.Name === 'None') opt.IsSelected = false;
            });
        }
        if (isMultiSelect) {
            target.IsSelected = isSelected;
        } else {
            options.forEach((opt, i) => {
                if (opt.IsArchived) return;
                opt.IsSelected = i === targetIdx ? isSelected : false;
            });
        }
    }
}

/** Recursively clears IsSelected on a SubOption and all of its nested content */
function clearSubOptionNested(subOption: any): any {
    return {
        ...subOption,
        IsSelected: false,
        SubOptions: subOption.SubOptions?.map(clearSubOptionNested) ?? [],
        SubQuestions: subOption.SubQuestions?.map(clearSubQuestionNested) ?? [],
    };
}

/** Clears IsSelected on all options within a sub-question and their nested content */
function clearSubQuestionNested(subQuestion: any): any {
    return {
        ...subQuestion,
        Options:
            subQuestion.Options?.map((opt: any) => ({
                ...opt,
                IsSelected: false,
                SubOptions: opt.SubOptions?.map(clearSubOptionNested) ?? [],
                SubQuestions: opt.SubQuestions?.map(clearSubQuestionNested) ?? [],
            })) ?? [],
    };
}

/**
 * Returns a new AssessmentCategory with all nested SubOptions and SubQuestions
 * within the "Yes" option of the specified question cleared (IsSelected = false).
 */
function clearYesOptionNested(category: AssessmentCategory, questionIndex: number): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const yesOption = question.Options?.find((o) => !o.IsArchived && o.Name === 'Yes');
    if (!yesOption || ((yesOption.SubOptions?.length ?? 0) === 0 && (yesOption.SubQuestions?.length ?? 0) === 0)) {
        return category;
    }

    const clearedYesOption = {
        ...yesOption,
        SubOptions: yesOption.SubOptions?.map(clearSubOptionNested) ?? [],
        SubQuestions: yesOption.SubQuestions?.map(clearSubQuestionNested) ?? [],
    };

    const updatedOptions = question.Options.map((opt) => (opt === yesOption ? clearedYesOption : opt));
    const updatedQuestion = { ...question, Options: updatedOptions };

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? updatedQuestion : q)),
    };
}

const CategoryQuestions: React.FC<{
    assessmentType: AssessmentTypeEnum;
    category: AssessmentCategory;
    onChange: (category: AssessmentCategory) => void;
    memberId: string;
}> = React.memo(({ assessmentType, category, onChange, memberId }) => {
    // Local state for comments to ensure immediate UI feedback and prevent Redux sync overwrites
    const [localCommentEntries, setLocalCommentEntries] = React.useState<CommentEntry[]>(category?.CommentsList ?? []);
    const [newCommentText, setNewCommentText] = React.useState('');
    const [isCommentDialogOpen, setIsCommentDialogOpen] = React.useState(false);
    const [editingCommentIndex, setEditingCommentIndex] = React.useState<number | null>(null);
    const [editCommentText, setEditCommentText] = React.useState('');
    const [deletingCommentIndex, setDeletingCommentIndex] = React.useState<number | null>(null);
    const authContext = React.useMemo(() => new Authorization(), []);

    // Sync local comments when category prop changes (e.g., from Redux/API)
    React.useEffect(() => {
        if (category?.CommentsList?.length) {
            setLocalCommentEntries(category.CommentsList);
            return;
        }

        setLocalCommentEntries([]);
    }, [category?.CommentsList, category?.Comments]);

    // Memoize expensive computations
    const options = React.useMemo(
        () =>
            category?.Questions?.filter((q) => Boolean(q.Options))
                ?.sort((a, b) => b.Options.length - a.Options.length)[0]
                ?.Options.filter((o) => !o.IsArchived) || [],
        [category?.Questions]
    );

    const showDatePicker = React.useMemo(() => assessmentType === AssessmentTypeEnum.PastSurgicalHistory, [assessmentType]);
    const scoreTotal = React.useMemo(() => {
        let score = 0;
        const mmseQuestionDescriptions = new Set([
            AssessmentQuestions.PhysicalExam_Time.toString(),
            AssessmentQuestions.PhysicalExam_Place.toString(),
            AssessmentQuestions.PhysicalExam_AskMemberToRepeat.toString(),
            AssessmentQuestions.PhysicalExam_Recall3Words.toString(),
            AssessmentQuestions.PhysicalExam_ChooseOne.toString(),
            AssessmentQuestions.PhysicalExam_Naming.toString(),
            AssessmentQuestions.PhysicalExam_Repetition.toString(),
            AssessmentQuestions.PhysicalExam_3StepCommand.toString(),
            AssessmentQuestions.PhysicalExam_Reading.toString(),
            AssessmentQuestions.PhysicalExam_Writing.toString(),
            AssessmentQuestions.PhysicalExam_Copying.toString(),
        ]);
        const optionScoreMap: Record<string, number> = {
            None: 0,
            Year: 1,
            Season: 1,
            Month: 1,
            Date: 1,
            Day: 1,
            State: 1,
            City: 1,
            Building: 1,
            'Floor/area': 1,
            'Type of place (clinic/home/etc.)': 1,
            Ball: 1,
            Flag: 1,
            Tree: 1,
            'Serial 7s from 100': 5,
            "Spell 'WORLD' backwards": 5,
            '0': 0,
            '1': 1,
            '2': 2,
            '3': 3,
        };

        category?.Questions?.forEach((q) => {
            q.Options?.forEach((opt) => {
                if (opt.IsSelected && opt.SubQuestions) {
                    opt.SubQuestions.forEach((subQ) => {
                        if (!mmseQuestionDescriptions.has(subQ.Description)) return;

                        subQ.Options?.forEach((subOpt) => {
                            if (subOpt.IsSelected) {
                                score += optionScoreMap[subOpt.Name] ?? 0;
                            }
                        });
                    });
                }
            });
        });
        return score;
    }, [category?.Questions]);

    const handleOnUpdateAnswer = (isSelected: boolean, questionIndex: number, optionIndex: number) => {
        const question = category.Questions[questionIndex];
        const visibleOptions = question.Options?.filter((o) => !o.IsArchived) ?? [];
        const hasNoneOption = visibleOptions.some((o) => o.Name === 'None');
        // When selecting a non-Yes option (e.g. No, Not Assessed), clear all nested
        // SubOptions and SubQuestions that live inside the Yes option.
        const selectedOptionName = visibleOptions[optionIndex]?.Name;
        const shouldClearYesNested = isSelected && selectedOptionName !== 'Yes';

        if (!hasNoneOption) {
            let updatedCategory = updateQuestionOption(category, questionIndex, optionIndex, isSelected);
            if (shouldClearYesNested) {
                updatedCategory = clearYesOptionNested(updatedCategory, questionIndex);
            }
            onChange(updatedCategory);
            return;
        }

        const data = copyObject(category);
        const dataVisibleOptions = data.Questions[questionIndex].Options?.filter((o: { IsArchived?: boolean }) => !o.IsArchived) ?? [];
        const isMultiSelect = question.SelectionType === SelectionType.MultiSelect;
        applyNoneToggle(dataVisibleOptions, optionIndex, isSelected, isMultiSelect);
        const resultData = shouldClearYesNested ? clearYesOptionNested(data, questionIndex) : data;
        onChange(resultData);
    };

    const handleOnUpdateDropdownAnswer = React.useCallback(
        (selectedValue: string | string[], questionIndex: number) => {
            const updatedCategory = updateDropdownAnswer(category, questionIndex, selectedValue);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubAnswer = React.useCallback(
        (isSelected: boolean, questionIndex: number, optionIndex: number, subOptionIndex: number) => {
            const updatedCategory = updateSubOption(category, questionIndex, optionIndex, subOptionIndex, isSelected);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateDate = React.useCallback(
        (date, questionIndex) => {
            var dateToIso = date ? date.toISOString() : null;
            const updatedCategory = updateQuestionDate(category, questionIndex, dateToIso);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleAddCommentEntry = React.useCallback(() => {
        const trimmedComment = newCommentText.trim();
        if (!trimmedComment) {
            return;
        }

        const createdBy = authContext.UserName || authContext.UserId || ProviderId || 'Unknown';
        const newEntry: CommentEntry = {
            Id: '',
            Comment: trimmedComment,
            DateCreated: new Date().toISOString(),
            CreatedBy: createdBy,
        };

        const updatedComments = [...(localCommentEntries ?? []), newEntry];
        setLocalCommentEntries(updatedComments);
        setNewCommentText('');

        const updatedCategory = updateCategoryComments(category, updatedComments);
        onChange(updatedCategory);
    }, [authContext, category, localCommentEntries, newCommentText, onChange]);

    const handleOpenCommentDialog = React.useCallback(() => {
        setIsCommentDialogOpen(true);
    }, []);

    const handleCloseCommentDialog = React.useCallback(() => {
        setIsCommentDialogOpen(false);
    }, []);

    const handleConfirmAddComment = React.useCallback(() => {
        const trimmedComment = newCommentText.trim();
        if (!trimmedComment) {
            return;
        }
        handleAddCommentEntry();
        setIsCommentDialogOpen(false);
    }, [handleAddCommentEntry, newCommentText]);

    const formatCommentDate = React.useCallback((dateValue?: string) => {
        if (!dateValue) return '';
        const parsed = new Date(dateValue);
        if (Number.isNaN(parsed.getTime())) return dateValue;
        return parsed.toLocaleString();
    }, []);

    const handleOpenEditDialog = React.useCallback(
        (index: number) => {
            setEditingCommentIndex(index);
            setEditCommentText(localCommentEntries[index]?.Comment ?? '');
        },
        [localCommentEntries]
    );

    const handleCloseEditDialog = React.useCallback(() => {
        setEditingCommentIndex(null);
        setEditCommentText('');
    }, []);

    const handleConfirmEdit = React.useCallback(() => {
        if (editingCommentIndex === null || !editCommentText.trim()) return;
        const editedBy = authContext.UserName || authContext.UserId || ProviderId || 'Unknown';
        const updatedComments = localCommentEntries.map((e, i) =>
            i === editingCommentIndex ? { ...e, Comment: editCommentText.trim(), CreatedBy: editedBy, DateCreated: new Date().toISOString() } : e
        );
        setLocalCommentEntries(updatedComments);
        const updatedCategory = updateCategoryComments(category, updatedComments);
        onChange(updatedCategory);
        setEditingCommentIndex(null);
        setEditCommentText('');
    }, [editingCommentIndex, editCommentText, localCommentEntries, authContext, category, onChange]);

    const handleOpenDeleteDialog = React.useCallback((index: number) => {
        setDeletingCommentIndex(index);
    }, []);

    const handleCloseDeleteDialog = React.useCallback(() => {
        setDeletingCommentIndex(null);
    }, []);

    const handleConfirmDelete = React.useCallback(() => {
        if (deletingCommentIndex === null) return;
        const updatedComments = localCommentEntries.filter((_, i) => i !== deletingCommentIndex);
        setLocalCommentEntries(updatedComments);
        const updatedCategory = updateCategoryComments(category, updatedComments);
        onChange(updatedCategory);
        setDeletingCommentIndex(null);
    }, [deletingCommentIndex, localCommentEntries, category, onChange]);

    const handleOnChangeAnswerText = React.useCallback(
        (answerText: string, questionIndex: number) => {
            const updatedCategory = updateQuestionAnswerText(category, questionIndex, answerText);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubQuestionAnswer = React.useCallback(
        (isSelected: boolean, questionIndex: number, optionIndex: number, subQuestionIndex: number, subQuestionOptionIndex: number) => {
            const subQuestion = category.Questions[questionIndex]?.Options?.[optionIndex]?.SubQuestions?.[subQuestionIndex];
            const hasNoneOption = subQuestion?.Options?.some((opt) => opt.Name === 'None');

            if (!hasNoneOption || !subQuestion?.Options) {
                const updatedCategory = updateSubQuestionOption(category, questionIndex, optionIndex, subQuestionIndex, subQuestionOptionIndex, isSelected);
                onChange(updatedCategory);
                return;
            }

            const data = copyObject(category);
            const dataOptions = data.Questions[questionIndex]?.Options?.[optionIndex]?.SubQuestions?.[subQuestionIndex]?.Options;
            if (!dataOptions) {
                onChange(data);
                return;
            }

            const selectionType =
                data.Questions[questionIndex]?.Options?.[optionIndex]?.SubQuestions?.[subQuestionIndex]?.SelectionType || SelectionType.SingleSelect;
            const isMultiSelect = selectionType === SelectionType.MultiSelect || selectionType === SelectionType.MultiSelectWithDropdown;
            applyNoneToggle(dataOptions, subQuestionOptionIndex, isSelected, isMultiSelect);
            onChange(data);
        },
        [category, onChange]
    );

    const handleOnUpdateSubOptionSubQuestionAnswer = React.useCallback(
        (isSelected: boolean, questionIndex: number, optionIndex: number, subOptionIndex: number, subQuestionIndex: number, subQuestionOptionIndex: number) => {
            const data = copyObject(category);
            const subQuestion = data.Questions[questionIndex]?.Options?.[optionIndex]?.SubOptions?.[subOptionIndex]?.SubQuestions?.[subQuestionIndex];
            const options = subQuestion?.Options;

            if (!options) {
                onChange(data);
                return;
            }

            const selectionType = subQuestion.SelectionType || SelectionType.SingleSelect;
            const isMultiSelect = selectionType === SelectionType.MultiSelect || selectionType === SelectionType.MultiSelectWithDropdown;
            applyNoneToggle(options, subQuestionOptionIndex, isSelected, isMultiSelect);
            onChange(data);
        },
        [category, onChange]
    );

    const handleOnUpdateSubSubAnswer = React.useCallback(
        (isSelected: boolean, questionIndex: number, optionIndex: number, subOptionIndex: number, subSubOptionIndex: number) => {
            const updatedCategory = updateSubSubOption(category, questionIndex, optionIndex, subOptionIndex, subSubOptionIndex, isSelected);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubQuestionSubOptionAnswer = React.useCallback(
        (isSelected: boolean, questionIndex: number, optionIndex: number, subQuestionIndex: number, subQuestionOptionIndex: number, subOptionIndex: number) => {
            const data = copyObject(category);
            const subQuestionOption =
                data.Questions[questionIndex]?.Options?.[optionIndex]?.SubQuestions?.[subQuestionIndex]?.Options?.[subQuestionOptionIndex];
            const subOptions = subQuestionOption?.SubOptions;

            if (!subOptions) {
                onChange(data);
                return;
            }

            const firstSubOption = subOptions[0];
            const subOptionSelectionType = firstSubOption?.SelectionType ?? SelectionType.SingleSelect;
            const isMultiSelect = subOptionSelectionType === SelectionType.MultiSelect;
            applyNoneToggle(subOptions, subOptionIndex, isSelected, isMultiSelect);
            onChange(data);
        },
        [category, onChange]
    );

    const handleOnUpdateSubOptionName = React.useCallback(
        (name: string, questionIndex: number, optionIndex: number, subOptionIndex: number) => {
            const updatedCategory = updateSubOptionTextAnswer(category, questionIndex, optionIndex, subOptionIndex, name);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubSubOptionName = React.useCallback(
        (name: string, questionIndex: number, optionIndex: number, subOptionIndex: number, subSubOptionIndex: number) => {
            const updatedCategory = updateSubSubOptionTextAnswer(category, questionIndex, optionIndex, subOptionIndex, subSubOptionIndex, name);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubQuestionAnswerText = React.useCallback(
        (value: string | Date | null, questionIndex: number, optionIndex: number, subQuestionIndex: number) => {
            const updatedCategory = updateSubQuestionAnswerText(category, questionIndex, optionIndex, subQuestionIndex, value || '');
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubOptionSubQuestionAnswerText = React.useCallback(
        (value: string | Date | null, questionIndex: number, optionIndex: number, subOptionIndex: number, subQuestionIndex: number) => {
            const textValue = value instanceof Date && !isNaN(value.getTime()) ? value.toISOString() : (value as string) || '';
            const updatedCategory = updateSubOptionSubQuestionAnswerText(category, questionIndex, optionIndex, subOptionIndex, subQuestionIndex, textValue);
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const handleOnUpdateSubQuestionOptionSubOptionName = React.useCallback(
        (value: string, questionIndex: number, optionIndex: number, subQuestionIndex: number, subQuestionOptionIndex: number, subOptionIndex: number) => {
            const updatedCategory = updateSubQuestionOptionSubOptionTextAnswer(
                category,
                questionIndex,
                optionIndex,
                subQuestionIndex,
                subQuestionOptionIndex,
                subOptionIndex,
                value
            );
            onChange(updatedCategory);
        },
        [category, onChange]
    );

    const visibleQuestions = React.useMemo(() => category?.Questions?.filter((question) => !question.IsArchived) ?? [], [category?.Questions]);
    const colSpan = React.useMemo(() => options.length + 1 + (showDatePicker ? 1 : 0), [options.length, showDatePicker]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Questions Table */}
                <TableContainer>
                    <Table>
                        <TableBody>
                            {visibleQuestions.map((question, qIndex) => (
                                <QuestionRow
                                    key={`${question.Id}_${qIndex}`}
                                    question={question}
                                    qIndex={qIndex}
                                    colSpan={colSpan}
                                    showDatePicker={showDatePicker}
                                    scoreTotal={scoreTotal}
                                    onUpdateAnswer={handleOnUpdateAnswer}
                                    onUpdateDropdownAnswer={handleOnUpdateDropdownAnswer}
                                    onUpdateDate={handleOnUpdateDate}
                                    onUpdateSubAnswer={handleOnUpdateSubAnswer}
                                    onUpdateSubOptionName={handleOnUpdateSubOptionName}
                                    onUpdateSubSubAnswer={handleOnUpdateSubSubAnswer}
                                    onUpdateSubSubOptionName={handleOnUpdateSubSubOptionName}
                                    onUpdateSubOptionSubQuestionAnswerText={handleOnUpdateSubOptionSubQuestionAnswerText}
                                    onUpdateSubOptionSubQuestionAnswer={handleOnUpdateSubOptionSubQuestionAnswer}
                                    onUpdateSubQuestionAnswer={handleOnUpdateSubQuestionAnswer}
                                    onUpdateSubQuestionAnswerText={handleOnUpdateSubQuestionAnswerText}
                                    onUpdateSubQuestionSubOptionAnswer={handleOnUpdateSubQuestionSubOptionAnswer}
                                    onUpdateSubQuestionOptionSubOptionName={handleOnUpdateSubQuestionOptionSubOptionName}
                                    onChangeAnswerText={handleOnChangeAnswerText}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Comments Section */}
                <Paper elevation={3} sx={{ mt: 3, p: 2 }}>
                    <Box>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                            <Typography variant="body1" color="primary.main" fontWeight={600}>
                                {category.Name} Comments
                            </Typography>
                            <Button variant="contained" size="small" onClick={handleOpenCommentDialog}>
                                Add Comment
                            </Button>
                        </Stack>
                        <Divider flexItem sx={{ my: 2 }} />
                        <Dialog open={isCommentDialogOpen} onClose={handleCloseCommentDialog} fullWidth maxWidth="sm">
                            <DialogTitle>Add Comment</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    placeholder="Enter a comment..."
                                    value={newCommentText}
                                    sx={{ ...classes.commentTextarea, mt: 1 }}
                                    multiline
                                    onChange={(e) => setNewCommentText(e.currentTarget.value)}
                                    rows={4}
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button variant="outlined" onClick={handleCloseCommentDialog}>
                                    Cancel
                                </Button>
                                <Button variant="contained" onClick={handleConfirmAddComment}>
                                    Ok
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog open={editingCommentIndex !== null} onClose={handleCloseEditDialog} fullWidth maxWidth="sm">
                            <DialogTitle>Edit Comment</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    placeholder="Edit comment..."
                                    value={editCommentText}
                                    sx={{ ...classes.commentTextarea, mt: 1 }}
                                    multiline
                                    onChange={(e) => setEditCommentText(e.currentTarget.value)}
                                    rows={4}
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button variant="outlined" onClick={handleCloseEditDialog}>
                                    Cancel
                                </Button>
                                <Button variant="contained" onClick={handleConfirmEdit} disabled={!editCommentText.trim()}>
                                    Save
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog open={deletingCommentIndex !== null} onClose={handleCloseDeleteDialog} maxWidth="xs">
                            <DialogTitle>Delete Comment</DialogTitle>
                            <DialogContent>
                                <Typography>Are you sure you want to delete this comment? This action cannot be undone.</Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="outlined" onClick={handleCloseDeleteDialog}>
                                    Cancel
                                </Button>
                                <Button variant="contained" color="error" onClick={handleConfirmDelete}>
                                    Delete
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {localCommentEntries?.length ? (
                            localCommentEntries.map((entry, index) => (
                                <Box key={entry.Id || `${entry.CreatedBy}_${entry.DateCreated}_${index}`} mb={1.5} p={1.5} sx={classes.commentBox}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {entry.CreatedBy}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {formatCommentDate(entry.DateCreated)}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" onClick={() => handleOpenEditDialog(index)} title="Edit comment">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleOpenDeleteDialog(index)} title="Delete comment">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                    <Typography variant="body2" mt={0.5}>
                                        {entry.Comment}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No comments yet.
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>
        </LocalizationProvider>
    );
});

export default CategoryQuestions;
